import { sortBy } from "lodash";
import { useContext, useEffect, useState } from "react";

import {
  getAlignmentsSafely,
  gql,
  parseSearchResults,
  queries,
} from "@examspark/api";
import {
  BucketAggregation,
  Resource,
  SearchableResourceFilterInput,
} from "@examspark/types";

import { gqlQuery } from "../../../../api/util";
import { NewActivityContext } from "../../../../classroom";
import { useAuthorizationContext } from "../../../../hooks/useAuthorizationContext";
import { useAppContext } from "../../../../providers/AppContext";

export const useSearchPassages = () => {
  const [facets, setFacets] = useState<BucketAggregation[]>();
  const [objectiveFacets, setObjectiveFacets] =
    useState<
      { label: string; value: string; count: number; sequence: number }[]
    >();
  const [genreFacets, setGenreFacets] =
    useState<{ label: string; value: string; count: number }[]>();
  const [results, setResults] = useState<Resource[]>();
  const [state] = useContext(NewActivityContext);
  const [isFetching, setIsFetching] = useState(false);
  const authorizationContext = useAuthorizationContext();
  const [appState] = useAppContext();
  const objectiveAuthorityIds = appState.userPrefs?.objectiveAuthorityIds;

  /**
   * This method first fetches all items matching a given objective scope,
   * scrapes all `contextId`s from results, and searches for those passages.
   *
   * @todo It would ultimately be desirable to materialize these passage <->
   * item objective relationships in the passage document in ElasticSearch.
   * */
  const fetchPassages = async () => {
    if (!state.objectives.length) return;
    if (!state.sections) return;
    if (!state.language) return;
    // if (!context.app?.workspaceId) return;

    setIsFetching(true);

    /**
     * fetch all eligible items to get all possible contextIds (and facet
     * counts) for all passages that align to items
     */
    const contextIdsForItems = parseSearchResults(
      (
        await gqlQuery<any>({
          query: queries.search.search({
            label: "GetContextIDsForItems",
          }),
          variables: {
            limit: 0,
            filter: {
              isDeleted: {
                ne: true,
              },
              or: state.objectives.map((section) => ({
                objectiveId: { eq: section.id },
              })),
              language: {
                eq: state.language,
              },
              sk: {
                eq: "item#v0",
              },
              and: [],
            },
            authorizationContext,
          },
        })
      ).data.search,
    );

    const facets = contextIdsForItems.aggregations?.contextIds?.buckets;
    setFacets(facets);
    const contextIds =
      facets.length > 0 ? facets.map((f: any) => f.key) : ["-1"];

    const fetchPassages = async (nextToken?: string) => {
      let _results: Resource[] = [];
      if (!state.sections) return _results;
      if (!state.language) return _results;
      // if (!context.app?.workspaceId) return _results;

      const result = await gqlQuery<any>({
        query: queries.search.search({
          label: "SearchPassages",
          fields: `
            id
            sk
            idInc
            name
            lexile
            lexileMin
            lexileMax
            wordCount
            ${gql.fragments.resource.tags}
            contextItems {
              id
              sk
              resource {
                alignments {
                  items {
                    id
                    sk
                    content
                    abbreviation
                    sequence
                    authorityId
                    scope
                  }
                }
              }
            }
          `,
        }),
        variables: {
          limit: 100,
          nextToken,
          authorizationContext,
          sort: [
            {
              field: "createdAt",
              direction: "asc",
            },
          ],
          filter: {
            or: contextIds.map((ctxId: string) => {
              return {
                id: {
                  eq: ctxId,
                },
              };
            }),
            isDeleted: {
              ne: true,
            },
            sk: {
              eq: "resource#v0",
            },
          },
        },
      });

      _results = _results.concat(result.data.search.items);
      if (result.data.search.nextToken) {
        _results = _results.concat(
          await fetchPassages(result.data.search.nextToken),
        );
      }

      return sortBy(_results, ["name"]);
    };

    /** find contexts that align to those items */
    const contexts = await fetchPassages();

    // console.log("passages", contexts);
    setResults(contexts);
    setIsFetching(false);
  };

  useEffect(() => {
    // console.log("updating objective facets");
    const _objectiveFacets: {
      label: string;
      value: string;
      count: number;
      sequence: number;
      authorityId: string;
    }[] = [];
    const _genreFacets: {
      label: string;
      value: string;
      count: number;
    }[] = [];
    if (!results) {
      // setObjectiveFacets(_facets);
      return;
    }

    for (const passage of results) {
      for (const tag of passage.tags || []) {
        if (tag?.key !== "genre") continue;
        const facetIndex = _genreFacets.findIndex((facet) => {
          return facet.value === tag.value;
        });
        if (facetIndex > -1) {
          _genreFacets[facetIndex].count++;
        } else {
          _genreFacets.push({
            value: tag.value || "",
            label: tag.value || "",
            count: 1,
          });
        }
      }

      if (passage.contextItems) {
        for (const item of passage.contextItems) {
          for (const obj of getAlignmentsSafely(item?.resource?.alignments)) {
            if (
              objectiveAuthorityIds?.length &&
              obj.authorityId &&
              !objectiveAuthorityIds.includes(obj.authorityId)
            ) {
              continue;
            }

            const findIndex = _objectiveFacets.findIndex((facet) => {
              return facet.value === obj.id;
            });
            if (findIndex !== -1) {
              _objectiveFacets[findIndex].count += 1;
            } else {
              // console.log("adding");
              _objectiveFacets.push({
                label: obj.abbreviation ?? "",
                value: obj.id,
                sequence: obj.sequence ?? 0,
                count: 1,
                authorityId: obj.authorityId ?? "",
              });
            }
          }
        }
      }
    }

    // console.log("facets", _facets);
    setGenreFacets(_genreFacets);
    setObjectiveFacets(_objectiveFacets);
  }, [objectiveAuthorityIds, results]);

  /**
   * Given a set of passage (context) IDs, return the number of items available
   * to the user, considering all presently configured filters.
   */
  const fetchPassageFacets = async () => {
    if (!state.language) return;
    // if (!context.app?.workspaceId) return;

    const filter: SearchableResourceFilterInput = {
      isDeleted: {
        ne: true,
      },
      // workspaceId: {
      //   matchPhrasePrefix: context.app.workspaceId,
      // },
      language: {
        eq: state.language,
      },
      objectiveDocument: {
        eq: state.document?.replace("document#", ""),
      },
      interaction: {
        ne: "text-extended",
      },
      sk: {
        eq: "item#v0",
      },
      and: [
        {
          or: state.passages.map((ctx) => ({
            contextIds: {
              eq: ctx.id,
            },
          })),
        },
      ],
    };

    if (!state.isIncludeAllPassageItemsEnabled) {
      filter.and?.push({
        or: state.objectives.map((obj) => ({
          objectiveId: {
            eq: obj.id,
          },
        })),
      });
    }

    const result = await gqlQuery<any>({
      query: /* GraphQL */ `
        query SearchUserResources(
          $filter: SearchableResourceFilterInput
          $sort: [SearchableResourceSortInput!]
          $aggs: [SearchableResourceAggsInput!]
          $limit: Int
          $nextToken: String
        ) {
          searchUserResources(
            filter: $filter
            sort: $sort
            aggs: $aggs
            limit: $limit
            nextToken: $nextToken
          ) {
            nextToken
            aggregations {
              alignmentIds {
                key
                count
              }
              contextIds {
                key
                count
              }
            }
            total
            __typename
          }
        }
      `,
      variables: {
        filter,
      },
    });

    const aggregations = result.data.searchUserResources.aggregations;
    // console.log("Passage Facet Counts", aggregations);
    return aggregations;
  };

  return {
    facets,
    fetchPassageFacets: fetchPassageFacets,
    fetchPassages: fetchPassages,
    genreFacets,
    isFetching,
    objectiveFacets,
    results,
  };
};
